import type { CSSProperties } from 'react';
import { isDarkColor } from '@elfsight/lib-utils';
import {
  FreeLink,
  type FreeLinkConfig,
  pickFreeLinkSettings
} from '@elfsight/widget-free-link';
import { useSettings } from '@wu/widget-core';
import type { AudioPlayerSettings, Position } from '../../../types';
import { meta } from '../../constants';
import { checkIfFloating } from '../../helpers/checkIfFloating';
import { useTheme } from './ThemeProvider';

export const FREE_LINK_HEIGHT = 22;
const NARROW_WIDGET_WIDTH = 330;

const MINIMIZE_BUTTON_TEXT_MODE_PROPS: FreeLinkConfig['textModeProps'] = {
  customColor: 'rgba(113, 113, 113, 0.8)',
  fontSize: 9,
  forceTwoLines: true
};

export function StyledFreeLink({
  isMobileView = false
}: {
  isMobileView?: boolean;
}) {
  const { layout, position, minimizeButton, widgetWidth, ...settings } =
    useSettings();
  const freeLinkSettings = pickFreeLinkSettings(settings);
  const { textColor } = useTheme();

  const isNarrowWidgetForFreeLink = isNarrowWidget(widgetWidth);

  const isCenter = position === 'bottomCenter';
  const isFloating = checkIfFloating(layout);
  const inBackgroundLayout = ['floatingBackground', 'embedBackground'].includes(
    layout
  );

  const textModeProps = {
    color: textColor,
    ...(minimizeButton && !isMobileView && MINIMIZE_BUTTON_TEXT_MODE_PROPS)
  };

  const iconHidden = !isMobileView && (inBackgroundLayout || minimizeButton);

  const styles = createFreeLinkStyles({
    textColor,
    isCenter,
    isFloating,
    isMobileView,
    position,
    minimizeButton,
    inBackgroundLayout,
    isNarrowWidgetForFreeLink
  });

  return (
    <FreeLink
      {...freeLinkSettings}
      freeLinkAnchor={undefined} // use default template for anchor text
      appAlias={meta.alias}
      appName={meta.name}
      mode={isFloating ? 'text' : 'button'}
      textModeProps={textModeProps}
      iconHidden={iconHidden}
      style={styles}
    />
  );
}

type CreateFreeLinkStylesProps = {
  textColor: string;
  isCenter: boolean;
  isFloating: boolean;
  isMobileView: boolean;
  position: Position;
  minimizeButton: boolean;
  inBackgroundLayout: boolean;
  isNarrowWidgetForFreeLink: boolean;
};

const LEFT_POSITION_STYLE: CSSProperties = {
  justifyContent: 'left',
  textAlign: 'left',
  paddingLeft: 0
};

const RIGHT_POSITION_STYLE: CSSProperties = {
  justifyContent: 'right',
  textAlign: 'right',
  paddingRight: 0
};

function createFreeLinkStyles({
  textColor,
  isCenter,
  isFloating,
  isMobileView,
  position,
  minimizeButton,
  inBackgroundLayout,
  isNarrowWidgetForFreeLink
}: CreateFreeLinkStylesProps): FreeLinkConfig['style'] {
  if (isMobileView) {
    return createBarViewStyles(textColor);
  }

  if (isFloating && !inBackgroundLayout) {
    if (minimizeButton) {
      const currentPosition = isCenter ? 'bottomRight' : position;

      return createMinimizedFloatingStyles(currentPosition);
    }

    if (isCenter) {
      return createBarViewStyles(textColor);
    }

    return createDefaultFloatingStyles(isNarrowWidgetForFreeLink);
  }

  return createEmbedStyles();
}

function createBarViewStyles(textColor: string): FreeLinkConfig['style'] {
  const backgroundAlphaValue = isDarkColor(textColor) ? 5 : 10;

  return {
    link: {
      position: 'absolute',
      bottom: 0,
      height: '22px',
      width: '100%',
      maxWidth: 'unset',
      margin: 0,
      padding: 0,
      zIndex: 9999999999,
      background: `rgb(from ${textColor} r g b / ${backgroundAlphaValue}%)`,
      backdropFilter: 'blur(40px)'
    }
  };
}

function getPositionAlignment(position: Position): CSSProperties {
  const positionLower = position.toLowerCase();

  if (positionLower.includes('left')) {
    return LEFT_POSITION_STYLE;
  }

  if (positionLower.includes('right')) {
    return RIGHT_POSITION_STYLE;
  }

  return {};
}

function createMinimizedFloatingStyles(
  position: Position
): FreeLinkConfig['style'] {
  const positionAlignment = getPositionAlignment(position);

  return {
    link: {
      position: 'absolute',
      top: '100%',
      width: '100%',
      ...positionAlignment
    }
  };
}

function createDefaultFloatingStyles(
  isNarrowWidgetForFreeLink: boolean
): FreeLinkConfig['style'] {
  return {
    link: {
      width: '100%',
      maxWidth: '100%',
      paddingInline: '12px',
      marginBlock: '16px'
    },
    removeText: {
      ...(!isNarrowWidgetForFreeLink && {
        marginLeft: 'auto'
      })
    }
  };
}

function createEmbedStyles(): FreeLinkConfig['style'] {
  return {
    link: {
      marginTop: '12px'
    }
  };
}

function isNarrowWidget(
  widgetWidth: AudioPlayerSettings['widgetWidth']
): boolean {
  if (typeof widgetWidth === 'string' && widgetWidth.includes('%')) {
    return parseInt(widgetWidth.replace('%', ''), 10) < 10;
  } else if (typeof widgetWidth === 'string' && widgetWidth.includes('px')) {
    return parseInt(widgetWidth.replace('px', ''), 10) < NARROW_WIDGET_WIDTH;
  }

  return parseInt(widgetWidth.toString(), 10) < NARROW_WIDGET_WIDTH;
}
